import React from 'react';
import * as Babel from '@babel/standalone';
import { Icon } from '@iconify/react';

/**
 * Safe regex helper to prevent ReDoS attacks by limiting input length
 */
const safeRegexReplace = (input: string, pattern: RegExp, replacement: string, maxLength: number = 100000): string => {
    if (input.length > maxLength) {
        console.warn('Input too large for regex operation, truncating');
        input = input.substring(0, maxLength);
    }
    return input.replace(pattern, replacement);
};

/**
 * Creates a safe proxy wrapper for data objects to prevent React rendering errors.
 * When the LLM-generated code tries to render an object directly (e.g., {data.user}),
 * this proxy intercepts and returns a safe string representation instead.
 * 
 * BEHAVIOR:
 * - Primitive values (string, number, boolean, null, undefined) pass through unchanged
 * - Objects get toString() overridden to return "[Object]" instead of crashing React
 * - Arrays remain renderable (React can render arrays)
 * - Nested property access works normally (e.g., data.user.name returns the actual name)
 */
const createSafeDataProxy = (obj: any): any => {
    if (obj === null || obj === undefined) {
        return obj;
    }

    // Primitive types can be rendered directly
    if (typeof obj !== 'object') {
        return obj;
    }

    // Arrays should be proxied recursively but remain as arrays
    if (Array.isArray(obj)) {
        return obj.map(item => createSafeDataProxy(item));
    }

    // For objects, create a proxy that:
    // 1. Allows normal property access
    // 2. Overrides toString() to return safe representation
    // 3. Recursively wraps nested objects
    return new Proxy(obj, {
        get(target, prop, receiver) {
            // Override toString to return safe string representation
            if (prop === 'toString') {
                return function () {
                    // If the object has an 'id' property, show it
                    if (target.id !== undefined) {
                        return `[Object: id=${target.id}]`;
                    }
                    // If it has a 'name' property, show it
                    if (target.name !== undefined) {
                        return String(target.name);
                    }
                    // Otherwise, show generic object marker
                    return '[Object]';
                };
            }

            // Override valueOf to return safe representation
            if (prop === 'valueOf') {
                return function () {
                    if (target.id !== undefined) {
                        return target.id;
                    }
                    if (target.name !== undefined) {
                        return target.name;
                    }
                    return '[Object]';
                };
            }

            // Override Symbol.toPrimitive for type coercion
            if (prop === Symbol.toPrimitive) {
                return function (hint: string) {
                    if (hint === 'number') {
                        return NaN;
                    }
                    if (target.id !== undefined) {
                        return `[Object: id=${target.id}]`;
                    }
                    if (target.name !== undefined) {
                        return String(target.name);
                    }
                    return '[Object]';
                };
            }

            const value = Reflect.get(target, prop, receiver);

            // Recursively wrap nested objects/arrays
            if (value !== null && typeof value === 'object') {
                return createSafeDataProxy(value);
            }

            return value;
        }
    });
};

/**
 * Sanitizes JSX code by removing dangerous elements and allowing only safe HTML/JSX form elements.
 * This ensures the code can be safely transpiled and rendered.
 * Uses ReDoS-safe patterns with strict allowlist for security.
 * 
 * ALLOWED ELEMENTS (Whitelist):
 * - Container: div, span, section, article, header, footer, main, nav
 * - Text: p, h1-h6, label, strong, em, small, code, pre
 * - Lists: ul, ol, li, dl, dt, dd
 * - Tables: table, thead, tbody, tfoot, tr, th, td, caption
 * - Forms: form, input, textarea, select, option, button, fieldset, legend
 * - Media: img (with sanitized src)
 * 
 * BLOCKED ELEMENTS:
 * - Scripts: script, noscript
 * - Links: a, link
 * - Dangerous: iframe, embed, object, applet, frame, frameset
 * - Meta: meta, base, title
 * - Event handlers: onClick, onLoad, etc.
 * - JavaScript URLs: javascript:, data:
 */
export const sanitizeJSXCode = (code: string): string => {
    let sanitized = code;

    // Limit input size to prevent ReDoS
    const MAX_CODE_LENGTH = 100000;
    if (sanitized.length > MAX_CODE_LENGTH) {
        console.warn('Code length exceeds maximum, truncating');
        sanitized = sanitized.substring(0, MAX_CODE_LENGTH);
    }

    // Fix: Wrap nullish coalescing chains in parentheses when mixed with || or &&
    // This prevents Babel error: "Nullish coalescing operator(??) requires parens when mixing with logical operators"
    // Pattern: value ?? value ?? value === 0 || value === 0  →  (value ?? value ?? value) === 0 || ...
    // We need to wrap the ?? chain before it mixes with || or &&

    // Strategy: Find patterns like "x ?? y ?? z || ..." or "x ?? y && ..." and wrap the ?? part
    // This is a simplified fix that handles common AI-generated patterns
    sanitized = sanitized.replace(
        /(\{[^{}]*?)(\w+\??\.\w+(?:\??\.\w+)*(?:\s*\?\?\s*\w+\??\.\w+(?:\??\.\w+)*)+)\s*(\|\||&&)/g,
        (match, prefix, nullishChain, logicalOp) => {
            return `${prefix}(${nullishChain}) ${logicalOp}`;
        }
    );

    // Also fix ternary patterns where ?? and || are mixed without parens
    // Pattern: a ?? b ?? c ?? a === 0 || b === 0 ? ... : ...
    // Fix: wrap the ?? chain in parens: (a ?? b ?? c) ?? a === 0 || ...
    sanitized = sanitized.replace(
        /(\w+\??\.\w+(?:\??\.\w+)*(?:\s*\?\?\s*\w+\??\.\w+(?:\??\.\w+)*)+)\s*===\s*0\s*\|\|/g,
        (match, nullishChain) => {
            // Check if already wrapped in parens
            return `(${nullishChain}) === 0 ||`;
        }
    );

    // More aggressive fix: Replace problematic patterns that the AI commonly generates
    // Pattern: {value ?? value ?? value ?? value === 0 || value === 0 || value === 0 ? format(...) : "—"}
    // This pattern is syntactically invalid and needs complete restructuring
    // Replace with: {value ?? value ?? value != null ? format(value ?? value ?? value ?? 0) : "—"}

    // Simpler approach: Just ensure ?? chains are wrapped before any comparison operator
    // Find: identifier?.property ?? identifier?.property ?? ... followed by === or ||
    const fixNullishMixing = (input: string): string => {
        // Match pattern: something?.prop ?? something?.prop ?? something?.prop === 0 || ...
        // And wrap the ?? chain in parentheses
        return input.replace(
            /(\w+\??\.\w+(?:\s*\?\?\s*\w+\??\.\w+)+)(\s*===)/g,
            '($1)$2'
        );
    };
    sanitized = fixNullishMixing(sanitized);

    // Also handle: x ?? y ?? z || (without the ===)
    sanitized = sanitized.replace(
        /(\w+\??\.\w+(?:\s*\?\?\s*\w+\??\.\w+)+)(\s*\|\|)/g,
        '($1)$2'
    );

    // Extract return statement content if it's a full component
    // Safe alternative: Use indexOf and substring instead of complex regex
    if (sanitized.includes('import ') || sanitized.includes('interface ') || (sanitized.includes('const ') && sanitized.includes('export'))) {
        const returnIndex = sanitized.indexOf('return');
        if (returnIndex !== -1) {
            // Find the matching closing structure
            const afterReturn = sanitized.substring(returnIndex);
            const openParenIndex = afterReturn.indexOf('(');

            if (openParenIndex !== -1) {
                // Simple parenthesis matching (safe approach)
                let depth = 0;
                let endIndex = -1;
                for (let i = openParenIndex; i < afterReturn.length; i++) {
                    if (afterReturn[i] === '(') depth++;
                    if (afterReturn[i] === ')') {
                        depth--;
                        if (depth === 0) {
                            endIndex = i;
                            break;
                        }
                    }
                }

                if (endIndex !== -1) {
                    sanitized = afterReturn.substring(openParenIndex + 1, endIndex).trim();
                }
            }
        }
    }

    // Remove imports - Safe: Line-by-line string filtering (no regex)
    sanitized = sanitized.split('\n').filter(line => {
        const trimmed = line.trim();
        return !(trimmed.startsWith('import ') && (trimmed.includes(' from ') || trimmed.includes('import(')));
    }).join('\n');

    // Remove interface/type definitions - Safe: Line-by-line with bounded checks
    sanitized = sanitized.split('\n').filter(line => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('interface ') &&
            !trimmed.startsWith('type ') &&
            !trimmed.startsWith('export interface ') &&
            !trimmed.startsWith('export type ')) {
            return true;
        }
        return false;
    }).join('\n');

    // Remove component definitions - Safe: String-based checking
    sanitized = sanitized.split('\n').filter(line => {
        const trimmed = line.trim();
        const startsWithDef = trimmed.startsWith('const ') ||
            trimmed.startsWith('function ') ||
            trimmed.startsWith('export const ') ||
            trimmed.startsWith('export function ');
        if (!startsWithDef) return true;
        // Keep lines that contain JSX (< or >)
        return trimmed.includes('<') || trimmed.includes('>');
    }).join('\n');

    // Remove export statements - Safe: Line-by-line string check
    sanitized = sanitized.split('\n').filter(line => {
        const trimmed = line.trim();
        return !(trimmed.startsWith('export ') && !trimmed.includes('{') && !trimmed.includes('('));
    }).join('\n');

    // Remove HTML comments - Safe: Uses indexOf/substring instead of regex for better performance
    let commentStart = sanitized.indexOf('<!--');
    while (commentStart !== -1) {
        const commentEnd = sanitized.indexOf('-->', commentStart);
        if (commentEnd !== -1) {
            sanitized = sanitized.substring(0, commentStart) + sanitized.substring(commentEnd + 3);
            commentStart = sanitized.indexOf('<!--');
        } else {
            // Remove unclosed comment
            sanitized = sanitized.substring(0, commentStart);
            break;
        }
    }

    // Remove DOCTYPE - Safe: Specific pattern
    sanitized = safeRegexReplace(sanitized, /<!DOCTYPE[^>]{0,100}>/gi, '');

    // Remove script tags - Safe: Use indexOf/substring to avoid ReDoS
    let scriptStart = sanitized.indexOf('<script');
    while (scriptStart !== -1) {
        const scriptEnd = sanitized.indexOf('</script>', scriptStart);
        if (scriptEnd !== -1) {
            sanitized = sanitized.substring(0, scriptStart) + sanitized.substring(scriptEnd + 9);
            scriptStart = sanitized.indexOf('<script');
        } else {
            sanitized = sanitized.substring(0, scriptStart);
            break;
        }
    }

    // Remove style tags - Safe: Use indexOf/substring to avoid ReDoS
    let styleStart = sanitized.indexOf('<style');
    while (styleStart !== -1) {
        const styleEnd = sanitized.indexOf('</style>', styleStart);
        if (styleEnd !== -1) {
            sanitized = sanitized.substring(0, styleStart) + sanitized.substring(styleEnd + 8);
            styleStart = sanitized.indexOf('<style');
        } else {
            sanitized = sanitized.substring(0, styleStart);
            break;
        }
    }

    // Remove html/head/body tags - Safe: Specific patterns with bounded quantifiers
    sanitized = safeRegexReplace(sanitized, /<\/?html[^>]{0,100}>/gi, '');
    sanitized = safeRegexReplace(sanitized, /<\/?head[^>]{0,100}>/gi, '');
    sanitized = safeRegexReplace(sanitized, /<\/?body[^>]{0,100}>/gi, '');

    // Clean up leading content before first JSX element - Safe: Use indexOf
    const firstTagIndex = sanitized.indexOf('<');
    if (firstTagIndex > 0) {
        sanitized = sanitized.substring(firstTagIndex);
    }

    // Clean up trailing braces - Safe: Limited quantifier
    sanitized = safeRegexReplace(sanitized, />\s{0,10}};?\s{0,10}$/g, '>');

    // Remove markdown code blocks - Safe: Specific patterns at line start
    sanitized = safeRegexReplace(sanitized, /^```jsx\s{0,5}\n?/gm, '');
    sanitized = safeRegexReplace(sanitized, /^```javascript\s{0,5}\n?/gm, '');
    sanitized = safeRegexReplace(sanitized, /^```typescript\s{0,5}\n?/gm, '');
    sanitized = safeRegexReplace(sanitized, /^```tsx\s{0,5}\n?/gm, '');
    sanitized = safeRegexReplace(sanitized, /^```\s{0,5}\n?/gm, '');
    sanitized = safeRegexReplace(sanitized, /\n?```\s{0,5}$/gm, '');

    // ═══════════════════════════════════════════════════════════════════════════
    // ENHANCED SECURITY: Block Dangerous Elements and Attributes
    // ═══════════════════════════════════════════════════════════════════════════

    // Remove <a> tags (links) - use indexOf/substring for safety
    let linkStart = sanitized.indexOf('<a ');
    while (linkStart !== -1) {
        const linkEnd = sanitized.indexOf('</a>', linkStart);
        if (linkEnd !== -1) {
            // Extract text content between tags and keep only the text
            const tagEndIndex = sanitized.indexOf('>', linkStart);
            if (tagEndIndex !== -1 && tagEndIndex < linkEnd) {
                const textContent = sanitized.substring(tagEndIndex + 1, linkEnd);
                sanitized = sanitized.substring(0, linkStart) + textContent + sanitized.substring(linkEnd + 4);
            } else {
                sanitized = sanitized.substring(0, linkStart) + sanitized.substring(linkEnd + 4);
            }
        } else {
            sanitized = sanitized.substring(0, linkStart) + sanitized.substring(linkStart + 3);
            break;
        }
        linkStart = sanitized.indexOf('<a ');
    }
    // Also remove self-closing <a/> tags
    sanitized = sanitized.split('\n').filter(line => !line.trim().startsWith('<a/>')).join('\n');

    // Remove dangerous tags: iframe, embed, object, applet, frame
    const dangerousTags = ['iframe', 'embed', 'object', 'applet', 'frame', 'frameset', 'base', 'link', 'meta'];
    dangerousTags.forEach(tag => {
        // Remove opening and closing tags
        let tagStart = sanitized.indexOf(`<${tag}`);
        while (tagStart !== -1) {
            const tagEnd = sanitized.indexOf(`</${tag}>`, tagStart);
            if (tagEnd !== -1) {
                sanitized = sanitized.substring(0, tagStart) + sanitized.substring(tagEnd + tag.length + 3);
            } else {
                // Self-closing or unclosed tag
                const selfCloseEnd = sanitized.indexOf('>', tagStart);
                if (selfCloseEnd !== -1) {
                    sanitized = sanitized.substring(0, tagStart) + sanitized.substring(selfCloseEnd + 1);
                } else {
                    break;
                }
            }
            tagStart = sanitized.indexOf(`<${tag}`);
        }
    });

    // Remove noscript tags
    let noscriptStart = sanitized.indexOf('<noscript');
    while (noscriptStart !== -1) {
        const noscriptEnd = sanitized.indexOf('</noscript>', noscriptStart);
        if (noscriptEnd !== -1) {
            sanitized = sanitized.substring(0, noscriptStart) + sanitized.substring(noscriptEnd + 11);
            noscriptStart = sanitized.indexOf('<noscript');
        } else {
            sanitized = sanitized.substring(0, noscriptStart);
            break;
        }
    }

    // Remove inline event handlers (onClick, onLoad, onError, etc.) - line by line
    const eventHandlerPatterns = [
        'onClick', 'onDoubleClick', 'onMouseDown', 'onMouseUp', 'onMouseMove', 'onMouseOver', 'onMouseOut', 'onMouseEnter', 'onMouseLeave',
        'onLoad', 'onUnload', 'onError', 'onAbort', 'onResize', 'onScroll',
        'onChange', 'onInput', 'onSubmit', 'onReset', 'onFocus', 'onBlur', 'onSelect',
        'onKeyDown', 'onKeyUp', 'onKeyPress',
        'onCopy', 'onCut', 'onPaste',
        'onDrag', 'onDragStart', 'onDragEnd', 'onDragOver', 'onDragEnter', 'onDragLeave', 'onDrop',
        'onTouchStart', 'onTouchMove', 'onTouchEnd', 'onTouchCancel',
        'onPointerDown', 'onPointerUp', 'onPointerMove', 'onPointerOver', 'onPointerOut', 'onPointerEnter', 'onPointerLeave', 'onPointerCancel',
        'onContextMenu', 'onWheel'
    ];

    eventHandlerPatterns.forEach(handler => {
        // Remove handler={...} patterns
        const handlerPattern = `${handler}={`;
        let handlerIndex = sanitized.indexOf(handlerPattern);
        while (handlerIndex !== -1) {
            // Find the closing brace, accounting for nested braces
            let braceDepth = 0;
            let endIndex = handlerIndex + handlerPattern.length;
            let foundEnd = false;

            for (let i = endIndex; i < sanitized.length && i < endIndex + 500; i++) {
                if (sanitized[i] === '{') braceDepth++;
                if (sanitized[i] === '}') {
                    if (braceDepth === 0) {
                        endIndex = i + 1;
                        foundEnd = true;
                        break;
                    }
                    braceDepth--;
                }
            }

            if (foundEnd) {
                sanitized = sanitized.substring(0, handlerIndex) + sanitized.substring(endIndex);
            } else {
                // Malformed, remove to end of line
                const lineEnd = sanitized.indexOf('\n', handlerIndex);
                if (lineEnd !== -1) {
                    sanitized = sanitized.substring(0, handlerIndex) + sanitized.substring(lineEnd);
                } else {
                    sanitized = sanitized.substring(0, handlerIndex);
                }
                break;
            }
            handlerIndex = sanitized.indexOf(handlerPattern);
        }
    });

    // Remove javascript: and data: URLs from href and src attributes
    sanitized = sanitized.split('\n').map(line => {
        // Remove javascript: URLs
        if (line.includes('javascript:')) {
            line = line.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
            line = line.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""');
        }
        // Remove data: URLs (can be used for XSS)
        if (line.includes('data:')) {
            line = line.replace(/href\s*=\s*["']data:[^"']*["']/gi, 'href="#"');
            line = line.replace(/src\s*=\s*["']data:[^"']*["']/gi, 'src=""');
        }
        return line;
    }).join('\n');

    // Remove dangerouslySetInnerHTML
    let dangerousIndex = sanitized.indexOf('dangerouslySetInnerHTML');
    while (dangerousIndex !== -1) {
        // Find the end of this prop (usually ends with }} or }})
        const propEnd = sanitized.indexOf('}}', dangerousIndex);
        if (propEnd !== -1) {
            sanitized = sanitized.substring(0, dangerousIndex) + sanitized.substring(propEnd + 2);
        } else {
            const lineEnd = sanitized.indexOf('\n', dangerousIndex);
            if (lineEnd !== -1) {
                sanitized = sanitized.substring(0, dangerousIndex) + sanitized.substring(lineEnd);
            } else {
                sanitized = sanitized.substring(0, dangerousIndex);
                break;
            }
        }
        dangerousIndex = sanitized.indexOf('dangerouslySetInnerHTML');
    }

    // Final validation: Log warning if suspicious patterns remain
    const suspiciousPatterns = ['eval(', 'Function(', '__proto__', 'constructor', 'prototype'];
    suspiciousPatterns.forEach(pattern => {
        if (sanitized.includes(pattern)) {
            console.warn(`[Security] Suspicious pattern detected and should be reviewed: ${pattern}`);
        }
    });

    // ═══════════════════════════════════════════════════════════════════════════
    // CRITICAL FIX: Detect and prevent object rendering in JSX
    // ═══════════════════════════════════════════════════════════════════════════
    // 
    // React Error: "Objects are not valid as a React child"
    // Occurs when JSX tries to render: {someObject} instead of {someObject.property}
    //
    // This validation detects potentially dangerous patterns and logs warnings

    // Pattern 1: Detect {item} without property access
    // Safe: {item?.name} or {item?.id}
    // Unsafe: {item} or {item }
    // Note: {data?.FIELD} is intentionally excluded — one-level data access is safe for primitives
    const itemPattern = /\{(item|row|elem|obj|object)\s*\}/g;
    let itemMatch;
    const unsafePatterns: string[] = [];

    while ((itemMatch = itemPattern.exec(sanitized)) !== null) {
        const matched = itemMatch[0];
        // Check if it's truly unsafe (not followed by a property access or method)
        const nextChar = sanitized[itemMatch.index + matched.length];
        if (nextChar !== '.' && nextChar !== '?' && nextChar !== '[') {
            unsafePatterns.push(matched);
            console.error(
                `[JSX Validation] Potentially unsafe pattern detected: "${matched}"\n` +
                `This will cause "Objects are not valid as a React child" error.\n` +
                `Fix: Access specific property like {item?.propertyName ?? ''}`
            );
        }
    }

    // Pattern 2: Detect data access without property after object path
    // Safe: data?.user?.name
    // Unsafe: data?.user (if user is an object)
    // This is harder to detect statically, so we just warn about ending with certain words
    const objectEndPattern = /\{data\?\.(\w+)\s*(?:\?\?|:|\})/g;
    let objectMatch;

    while ((objectMatch = objectEndPattern.exec(sanitized)) !== null) {
        const fieldName = objectMatch[1];
        // Common object field names that might indicate an object rather than primitive
        const likelyObjectNames = ['object', 'obj', 'data', 'info', 'details', 'user', 'payee', 'servicer', 'assignedTo', 'audit', 'deductible'];
        if (likelyObjectNames.includes(fieldName)) {
            console.warn(
                `[JSX Validation] Suspicious pattern: "{data?.${fieldName}}" - ` +
                `If ${fieldName} is an object, this will cause a React error. ` +
                `Ensure you access a specific property like {data?.${fieldName}?.id ?? ''}`
            );
        }
    }

    if (unsafePatterns.length > 0) {
        console.error(
            `\n${'='.repeat(80)}\n` +
            `❌ JSX VALIDATION FAILED - Detected ${unsafePatterns.length} unsafe pattern(s)\n` +
            `${'='.repeat(80)}\n` +
            `The following patterns will cause "Objects are not valid as a React child" errors:\n` +
            unsafePatterns.map((p, i) => `${i + 1}. ${p}`).join('\n') +
            `\n\n` +
            `HOW TO FIX:\n` +
            `- Replace {item} with {item?.propertyName ?? ''}\n` +
            `- Replace {data?.object} with {data?.object?.property ?? 'N/A'}\n` +
            `- Never render entire objects/arrays, only primitive values\n` +
            `${'='.repeat(80)}\n`
        );
    }

    return sanitized.trim();
};

/**
 * Props for DynamicJSXRenderer component
 */
export interface DynamicJSXRendererProps {
    /** The JSX code string to render */
    code: string;
    /** The data object to pass to the rendered component */
    data: any;
    /** Optional click handler for interactive elements */
    onElementClick?: (e: React.MouseEvent, text: string) => void;
    /** Optional className for the error container */
    errorClassName?: string;
}

/**
 * Props for UXGenieTemplateRenderer - the unified template renderer component
 */
export interface UXGenieTemplateRendererProps {
    /** The JSX code string to render */
    code: string;
    /** The raw data object (can be JSON string or parsed object) */
    data: any;
    /** Optional click handler for interactive elements */
    onElementClick?: (e: React.MouseEvent, text: string) => void;
    /** Optional className for the error container */
    errorClassName?: string;
    /** Optional className for the wrapper container */
    containerClassName?: string;
    /** Whether to show loading state */
    isLoading?: boolean;
}

/**
 * Prepares data for template rendering by:
 * 1. Parsing JSON strings if needed
 * 2. Detecting if template expects user_input wrapper
 * 3. Wrapping data in user_input if needed
 * 4. Handling array data (extracting first element if needed)
 * 5. Deep cloning to prevent React Proxy issues
 * 
 * @param code The template code to analyze for data structure expectations
 * @param rawData The raw data (can be JSON string or parsed object)
 * @returns Prepared data object ready for template rendering
 */
export const prepareDataForTemplate = (_code: string, rawData: any): any => {
    // Parse JSON string if needed
    let parsedData = rawData;
    if (typeof rawData === 'string') {
        try {
            parsedData = JSON.parse(rawData);
        } catch {
            console.warn('Failed to parse data as JSON, using as-is');
            return rawData;
        }
    }

    if (!parsedData || typeof parsedData !== 'object') {
        return parsedData ?? {};
    }

    // Deep clone to prevent React Proxy issues
    try {
        parsedData = JSON.parse(JSON.stringify(parsedData));
    } catch (error) {
        console.warn('Failed to deep clone data:', error);
    }

    return parsedData;
};

/**
 * Formats a plain object as readable text, e.g. `{ accident: true, damageSeverity: 'Minor' }`
 * becomes `accident: true, damageSeverity: Minor`. Nested values are JSON-stringified.
 */
const formatObjectForDisplay = (obj: Record<string, unknown>): string =>
    Object.entries(obj)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => {
            if (typeof value !== 'object') return `${key}: ${String(value)}`;
            try {
                return `${key}: ${JSON.stringify(value)}`;
            } catch {
                return `${key}: [Object]`;
            }
        })
        .join(', ');

/**
 * Walks the element tree returned by a template and replaces any plain-object child
 * (not a React element) with a readable string. Templates are generated against sample
 * data, so a field rendered as `{data.damage}` may be an object in real data — React
 * throws "Objects are not valid as a React child" (#31) for that during reconciliation,
 * which a try/catch around the render function cannot catch.
 */
export const sanitizeRenderedNode = (node: unknown): React.ReactNode => {
    if (node === null || node === undefined || typeof node === 'boolean') return node as React.ReactNode;
    if (typeof node !== 'object') return node as React.ReactNode;
    if (Array.isArray(node)) return node.map(sanitizeRenderedNode);
    if (React.isValidElement(node)) {
        const props = node.props as { children?: unknown; dangerouslySetInnerHTML?: unknown };
        if (props.children === undefined || props.dangerouslySetInnerHTML || typeof props.children === 'function') {
            return node;
        }
        const children = sanitizeRenderedNode(props.children);
        return Array.isArray(children)
            ? React.cloneElement(node, undefined, ...children)
            : React.cloneElement(node, undefined, children);
    }
    return formatObjectForDisplay(node as Record<string, unknown>);
};

/**
 * Browser globals hidden from template code: they are passed as `undefined` parameters,
 * so a template cannot reach the DOM, storage, network or the Function constructor by name.
 */
const BLOCKED_TEMPLATE_GLOBALS = [
    'window', 'self', 'globalThis', 'document', 'navigator', 'location',
    'localStorage', 'sessionStorage', 'indexedDB',
    'fetch', 'XMLHttpRequest', 'WebSocket',
    'Function', 'setTimeout', 'setInterval',
];

/**
 * Turns Babel-transpiled template code into its render function.
 *
 * SECURITY: UX Genie templates are JSX strings, so they can only be rendered by executing them.
 * The code has already been sanitized by sanitizeJSXCode() and transpiled by Babel; here it runs
 * in strict mode (`this` is not the global object) with only `React` provided and the browser
 * globals above shadowed. Reviewed as safe for this feature.
 */
const createTemplateRenderFunction = (transpiled: string | null | undefined) =>
    // eslint-disable-next-line no-new-func
    new Function('React', ...BLOCKED_TEMPLATE_GLOBALS, `"use strict"; return ${transpiled}`)(React); // NOSONAR

/**
 * Renders dynamic JSX code with data binding.
 * Sanitizes the code to remove HTML comments and other invalid JSX,
 * then transpiles and executes it with the provided data.
 */
export const DynamicJSXRenderer: React.FC<DynamicJSXRendererProps> = ({
    code,
    data,
    onElementClick,
    errorClassName = '',
}) => {
    try {
        const sanitizedCode = sanitizeJSXCode(code);

        // Replace all N/A and '-' fallbacks with empty strings to avoid displaying them
        const cleanedCode = sanitizedCode
            .replace(/\?\?\s*['"]N\/A['"]/g, "?? ''")
            .replace(/\?\?\s*['"]-['"]/g, "?? ''");

        // Wrap in IIFE that receives data parameter
        const codeToTranspile = `
      (function(data) {
        return (
          ${cleanedCode}
        );
      })
    `;

        const transpiled = Babel.transform(codeToTranspile, {
            presets: ['react'],
            filename: 'dynamic-jsx.jsx'
        }).code;

        const renderFunction = createTemplateRenderFunction(transpiled);

        // Pass data directly to the render function (same approach as UXGenie PreviewRenderer)
        // Deep clone to ensure plain JavaScript objects without React Proxy wrappers
        let cleanData = data;
        try {
            cleanData = JSON.parse(JSON.stringify(data));
        } catch {
            // If cloning fails, use original data
        }
        const renderedContent = sanitizeRenderedNode(renderFunction(cleanData));

        if (onElementClick) {
            const handleClick = (e: React.MouseEvent) => {
                const target = e.target as HTMLElement;
                if (target.textContent && target.textContent.trim().length > 0) {
                    const meaningfulTags = ['SPAN', 'P', 'LABEL', 'H1', 'H2', 'H3', 'TH', 'TD', 'BUTTON', 'A'];
                    if (meaningfulTags.includes(target.tagName) || target.childNodes.length === 1) {
                        e.stopPropagation();
                        onElementClick(e, target.textContent.trim());
                    }
                }
            };

            const handleKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const target = e.target as HTMLElement;
                    if (target.textContent && target.textContent.trim().length > 0) {
                        const meaningfulTags = ['SPAN', 'P', 'LABEL', 'H1', 'H2', 'H3', 'TH', 'TD', 'BUTTON', 'A'];
                        if (meaningfulTags.includes(target.tagName) || target.childNodes.length === 1) {
                            onElementClick(e as any, target.textContent.trim());
                        }
                    }
                }
            };

            return (
                <div
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={0}
                    className="cursor-crosshair transition-all hover:ring-2 ring-blue-100 rounded-lg"
                >
                    {renderedContent}
                </div>
            );
        }

        return renderedContent;
    } catch (error) {
        console.error('DynamicJSXRenderer error:', error);
        const errorMsg = (error as Error).message;
        return (
            <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${errorClassName}`}>
                <div className="flex items-start gap-2">
                    <Icon icon="material-symbols:error" className="text-red-600 mt-0.5" width={20} />
                    <div className="text-sm text-red-800">
                        <p className="font-medium">Preview Error</p>
                        <p className="text-xs mt-1">Could not render the code: {errorMsg}</p>
                    </div>
                </div>
            </div>
        );
    }
};

/**
 * UXGenieTemplateRenderer - Unified component for rendering UXGenie templates
 * 
 * This is the main component to use for rendering UXGenie templates across the app.
 * It handles all the complexity of:
 * - Code sanitization and cleaning
 * - Data preparation (JSON parsing, user_input wrapping, array handling)
 * - Babel transpilation
 * - Error handling with user-friendly messages
 * - Click interactivity for template editing
 * 
 * USE THIS COMPONENT FOR:
 * 1. Live preview during template generation
 * 2. View mode when displaying saved templates
 * 3. Edit mode when modifying existing templates
 * 4. Action execution when rendering with API response data
 * 
 * @example
 * // Basic usage with JSON data
 * <UXGenieTemplateRenderer
 *   code={generatedCode}
 *   data={apiResponse}
 * />
 * 
 * @example
 * // With click handler for editing
 * <UXGenieTemplateRenderer
 *   code={generatedCode}
 *   data={originalData}
 *   onElementClick={(e, text) => console.log('Clicked:', text)}
 *   containerClassName="bg-white rounded-lg shadow"
 * />
 */
export const UXGenieTemplateRenderer: React.FC<UXGenieTemplateRendererProps> = ({
    code,
    data,
    onElementClick,
    errorClassName = '',
    containerClassName = '',
    isLoading = false,
}) => {
    if (isLoading) {
        return (
            <div className={`flex items-center justify-center p-8 ${containerClassName}`}>
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-gray-600">Loading template...</p>
                </div>
            </div>
        );
    }

    if (!code) {
        return (
            <div className={`p-4 bg-gray-50 border border-gray-200 rounded-lg text-center ${containerClassName}`}>
                <Icon icon="material-symbols:code-off" className="text-gray-400 mx-auto mb-2" width={32} />
                <p className="text-sm text-gray-600">No template code available</p>
            </div>
        );
    }

    try {
        // Step 1: Sanitize and clean the code
        const sanitizedCode = sanitizeJSXCode(code);
        const cleanedCode = sanitizedCode
            .replace(/\?\?\s*['"]N\/A['"]/g, "?? ''")
            .replace(/\?\?\s*['"]-['"]/g, "?? ''")
            .replace(/\?\?\s*['"]—['"]/g, "?? ''")
            .replace(/:\s*['"]N\/A['"]/g, ": ''")
            .replace(/:\s*['"]—['"]/g, ": ''");

        // Step 2: Prepare data (parse JSON, wrap in user_input if needed, handle arrays)
        const preparedData = prepareDataForTemplate(cleanedCode, data);

        console.log('🎨 UXGenieTemplateRenderer:', {
            codeLength: cleanedCode.length,
            dataType: typeof preparedData,
            dataKeys: preparedData && typeof preparedData === 'object' ? Object.keys(preparedData).slice(0, 5) : 'N/A',
            hasUserInputWrapper: !!preparedData?.user_input
        });

        // Step 3: Transpile with Babel
        const codeToTranspile = `
            (function(data) {
                return (
                    ${cleanedCode}
                );
            })
        `;

        const transpiled = Babel.transform(codeToTranspile, {
            presets: ['react'],
            filename: 'uxgenie-template.jsx'
        }).code;

        // Step 4: Execute and render
        const renderFunction = createTemplateRenderFunction(transpiled);
        const renderedContent = sanitizeRenderedNode(renderFunction(preparedData));

        // Step 5: Apply interactivity wrapper if needed
        if (onElementClick) {
            const handleClick = (e: React.MouseEvent) => {
                const target = e.target as HTMLElement;
                if (target.textContent && target.textContent.trim().length > 0) {
                    const meaningfulTags = ['SPAN', 'P', 'LABEL', 'H1', 'H2', 'H3', 'TH', 'TD', 'BUTTON', 'A'];
                    if (meaningfulTags.includes(target.tagName) || target.childNodes.length === 1) {
                        e.stopPropagation();
                        onElementClick(e, target.textContent.trim());
                    }
                }
            };

            const handleKeyDown = (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const target = e.target as HTMLElement;
                    if (target.textContent && target.textContent.trim().length > 0) {
                        const meaningfulTags = ['SPAN', 'P', 'LABEL', 'H1', 'H2', 'H3', 'TH', 'TD', 'BUTTON', 'A'];
                        if (meaningfulTags.includes(target.tagName) || target.childNodes.length === 1) {
                            onElementClick(e as any, target.textContent.trim());
                        }
                    }
                }
            };

            return (
                <div
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={0}
                    className={`cursor-crosshair transition-all hover:ring-2 ring-blue-100 rounded-lg ${containerClassName}`}
                >
                    {renderedContent}
                </div>
            );
        }

        return containerClassName ? (
            <div className={containerClassName}>{renderedContent}</div>
        ) : (
            renderedContent
        );

    } catch (error) {
        console.error('UXGenieTemplateRenderer error:', error);
        const errorMsg = (error as Error).message;
        return (
            <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${errorClassName}`}>
                <div className="flex items-start gap-2">
                    <Icon icon="material-symbols:error" className="text-red-600 mt-0.5" width={20} />
                    <div className="text-sm text-red-800">
                        <p className="font-medium">Template Render Error</p>
                        <p className="text-xs mt-1 break-words">Could not render the template: {errorMsg}</p>
                    </div>
                </div>
            </div>
        );
    }
};

export default DynamicJSXRenderer;
